import axios from 'axios';
import apiClient from './api.service';
import { DashboardData } from '../types/participant.types';
import { PublicSurveyPageData } from '../types/publicSurvey.types';

/**
 * Participant API Service
 * 
 * Handles all API calls for the participant dashboard feature.
 * Provides methods for fetching dashboard data.
 */

class ParticipantService {
  private apiClient = apiClient;

  /**
   * Fetch paginated ACTIVE surveys for participant tabs.
   * Includes canParticipate=false when the participant has already answered.
   */
  async getActiveSurveys(page: number = 1, pageSize: number = 10, tagIds: string[] = []): Promise<PublicSurveyPageData> {
    try {
      const response = await this.apiClient.get<{ data: PublicSurveyPageData }>(
        '/api/participant/surveys/active',
        { params: { page, pageSize, tagIds: tagIds.length > 0 ? tagIds : undefined } },
      );

      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Fetch complete dashboard data
   * 
   * Calls GET /api/participant/dashboard with optional pagination parameters
   * 
   * @param page - Page number for completed surveys (default: 1)
   * @param pageSize - Items per page for completed surveys (default: 10)
   * @returns DashboardData containing profile, openSurveys, and completedSurveys
   */
  async getDashboard(page: number = 1, pageSize: number = 10, tagIds: string[] = []): Promise<DashboardData> {
    try {
      const response = await this.apiClient.get<{ data: DashboardData }>('/api/participant/dashboard', {
        params: {
          page,
          pageSize,
          tagIds: tagIds.length > 0 ? tagIds : undefined,
        },
      });

      return response.data.data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Validate survey status before opening
   * 
   * Called when user clicks "Responder" button to ensure survey is still active.
   * FR-007a: Validate Survey.status=ACTIVE and current date within startDate/endDate
   * 
   * @param surveyId - The survey ID to validate
   * @returns true if survey is active and available, false otherwise
   */
  async validateSurveyStatus(surveyId: string): Promise<boolean> {
    try {
      // Call backend to validate survey is still active and within date range
      const response = await this.apiClient.get(`/api/surveys/${surveyId}/status`);
      
      // Backend should return { isActive: boolean, reason?: string }
    const { isActive } = response.data.data;
      
      return isActive;
    } catch (error) {
      console.error(`Error validating survey ${surveyId}:`, error);
      // If validation fails (404, 500, etc.), assume survey not available
      return false;
    }
  }

  /**
   * Fetch paginated COMPLETED surveys the participant has NOT answered.
   * Used for the "Pesquisas Não Respondidas" tab.
   */
  async getUnansweredCompleted(page: number = 1, pageSize: number = 10, tagIds: string[] = []): Promise<PublicSurveyPageData> {
    try {
      const response = await this.apiClient.get<{ data: PublicSurveyPageData }>(
        '/api/participant/surveys/unanswered-completed',
        { params: { page, pageSize, tagIds: tagIds.length > 0 ? tagIds : undefined } },
      );
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   * @param error - The error object from axios
   * @returns User-friendly error message
   */
  private handleError(error: unknown): Error {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        return new Error('Sessão expirada. Por favor, faça login novamente.');
      }
      if (error.response?.status === 403) {
        return new Error('Você não tem permissão para acessar este recurso.');
      }
      if (error.response?.status === 404) {
        return new Error('Recurso não encontrado.');
      }
      if (error.response?.status === 500) {
        return new Error('Erro no servidor. Por favor, tente novamente mais tarde.');
      }
      return new Error(error.response?.data?.error?.message || 'Erro ao buscar dados.');
    }
    return new Error('Erro de conexão. Por favor, verifique sua internet.');
  }
}

// Export singleton instance
export const participantService = new ParticipantService();

// Export class for testing
export { ParticipantService };
